import { useState, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { MapCategory } from '@/services/types';
import { usePostHog } from 'posthog-js/react';

interface CategoryDropdownProps {
  categories: MapCategory[];
  selectedCategoryId: string;
  excludeCategoryId?: string;
  onCategoryChange: (category: {id: string, name: string}) => void;
  isLoading?: boolean;
  color: 'pink' | 'blue';
  label: string;
  allowClear?: boolean;
}

function CategoryDropdown({
  categories,
  selectedCategoryId,
  excludeCategoryId,
  onCategoryChange,
  isLoading = false,
  color,
  label,
  allowClear = false
}: CategoryDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const posthog = usePostHog();
  
  const selectedCategory = categories.find(cat => cat.id === selectedCategoryId);
  const displayText = selectedCategory?.name || 'Select Category';
  const hasSelection = selectedCategoryId && selectedCategoryId !== '';

  // Filter out the excluded category from options
  const availableCategories = useMemo(() => {
    return categories.filter(cat => cat.id !== excludeCategoryId);
  }, [categories, excludeCategoryId]);

  const colorClasses = {
    pink: {
      bg: 'bg-[#f471b5]',
      hover: 'hover:bg-[#e55ba3]',
      focus: 'focus:ring-[#f471b5]'
    },
    blue: {
      bg: 'bg-[#f59e0b]',
      hover: 'hover:bg-[#d97706]',
      focus: 'focus:ring-[#f59e0b]'
    }
  };

  const colors = colorClasses[color];

  if (isLoading) {
    return (
      <div className='h-10 w-40 animate-pulse rounded-full bg-gray-200'></div>
    );
  }

  return (
    <div className='relative'>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button 
            className={cn(
              'flex items-center justify-center gap-2 rounded-full border border-gray-300 px-2 py-1 font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              colors.bg,
              colors.hover,
              colors.focus
            )}
            disabled={availableCategories.length === 0}
            aria-label={`Select ${label}. Currently selected: ${displayText}`}
          >
            <span>{displayText}</span>
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform duration-200',
                isOpen && 'rotate-180'
              )}
            />
          </button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent
          align='start'
          className='w-48 bg-white border border-gray-200 rounded-lg shadow-lg p-2'
        >
          {availableCategories.length === 0 ? (
            <div className='px-3 py-2 text-sm text-gray-500 text-center'>
              No categories available
            </div>
          ) : (
            <>
              {allowClear && hasSelection && (
                <button
                  onClick={() => {
                    posthog?.capture('filter_applied', { filter_type: 'map_category', label, value: '' });
                    onCategoryChange({id: '', name: ''});
                    setIsOpen(false);
                  }}
                  className='w-full rounded-md px-3 py-2 text-sm font-medium text-left transition-colors text-gray-500 hover:bg-red-50 hover:text-red-600 focus:bg-red-50 focus:outline-none border-b border-gray-200 mb-1'
                  role="option"
                >
                  Clear selection
                </button>
              )}
              {availableCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    posthog?.capture('filter_applied', { filter_type: 'map_category', label, value: category.name });
                    onCategoryChange({id: category.id, name: category.name});
                    setIsOpen(false);
                  }}
                  className={cn(
                    'w-full rounded-md px-3 py-2 text-sm font-medium text-left transition-colors',
                    selectedCategoryId === category.id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none'
                  )}
                  aria-selected={selectedCategoryId === category.id}
                  role="option"
                >
                  {category.name}
                </button>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

interface FilterDropdownProps {
  categories: MapCategory[];
  selectedCategory1: {id: string, name: string};
  selectedCategory2: {id: string, name: string};
  onCategory1Change: (category: {id: string, name: string}) => void;
  onCategory2Change: (category: {id: string, name: string}) => void;
  isLoading?: boolean;
}

export function FilterDropdowns({
  categories,
  selectedCategory1,
  selectedCategory2,
  onCategory1Change,
  onCategory2Change,
  isLoading = false
}: FilterDropdownProps) {
  if (isLoading) {
    return (
      <div className='sort-stakeholders-btn flex flex-col items-center gap-2 p-4 md:flex-row'>
        <p>Map</p>
        <p className="inline-flex items-center gap-2 rounded-full bg-[#60A5FA] px-2 py-1 font-medium font-grotesk text-gray-900">
          Organisations
        </p>
        <p>by</p> 
        <div className='flex items-center gap-2'>
          <div className='h-10 w-32 animate-pulse rounded-full bg-gray-200'></div>
          <div className='h-10 w-32 animate-pulse rounded-full bg-gray-200'></div>
        </div>
      </div>
    );
  }

  return (
    <div className='sort-stakeholders-btn flex flex-col items-center gap-2 p-4 md:flex-row'>
      <p>Map</p>
      <p className="inline-flex items-center gap-2 rounded-full bg-[#60A5FA] px-2 py-1 font-medium font-grotesk text-gray-900">
        Organisations
      </p>
      <p>by</p> 
      <div className='flex items-center gap-2'>
        <CategoryDropdown
          categories={categories}
          selectedCategoryId={selectedCategory1.id}
          excludeCategoryId={selectedCategory2.id}
          onCategoryChange={onCategory1Change}
          isLoading={isLoading}
          color="pink"
          label="Category 1"
          allowClear={true}
        />
        <CategoryDropdown
          categories={categories}
          selectedCategoryId={selectedCategory2.id}
          excludeCategoryId={selectedCategory1.id}
          onCategoryChange={onCategory2Change}
          isLoading={isLoading}
          color="blue"
          label="Category 2"
          allowClear={true}
        />
      </div>
    </div>
  );
}
